import { conditionalRender } from '@repo/toolkit/conditional-render'
import InnerBox from '@repo/ui/inner-box'
import { useQuery } from '@tanstack/react-query'
import { useFormContext } from 'react-hook-form'

import {
  getCountriesList,
  getCountriesListQueryKey
} from '../../../api/countries/get-countries-list'
import useTranslate from '../../../lib/hooks/use-translate'
import { getCountryLabel } from '../../../lib/types/models/country/country-list'
import { CseInput } from '../../../lib/types/models/cse/cse'
import { FormControl, FormField, FormItem } from '../../elements/forms/form'
import { ControlledAdressInput } from '../../elements/inputs/address-input'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'

const CseAddressFormSection = () => {
  const { currentLanguage } = useTranslate()

  const { data: countries } = useQuery({
    queryKey: getCountriesListQueryKey(),
    queryFn: () => getCountriesList()
  })
  const form = useFormContext<CseInput>()
  return (
    <InnerBox title="Addresse" className="rounded-xl">
      <div className="flex flex-col gap-2">
        <FormField
          control={form.control}
          name="address1"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledAdressInput
                  {...field}
                  required
                  label={'Adresse'}
                  onDetailsChange={(placeDetails) => {
                    form.setValue('address1', placeDetails.address, {
                      shouldValidate: true
                    })
                    if (placeDetails.postalCode)
                      form.setValue('zipCode', placeDetails.postalCode, {
                        shouldValidate: true
                      })
                    form.setValue('city', placeDetails.city, { shouldValidate: true })
                    // search for the country in the list of countries
                    const country = countries?.data.find(
                      (country) =>
                        getCountryLabel(currentLanguage, country) === placeDetails.country
                    )
                    if (country)
                      form.setValue('country', country.countryCode, {
                        shouldValidate: true
                      })
                    form.trigger(['address1', 'city', 'country'])
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="address2"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledInput
                  {...field}
                  label="Complément d'adresse"
                  type="textarea"
                  rows={3}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex justify-between gap-3">
          <FormField
            control={form.control}
            name="zipCode"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput {...field} required label="Code postal" type="text" />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput {...field} required label="Ville" type="text" />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="country"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledSelectInput
                  {...field}
                  required
                  searchable
                  options={countries?.data.map((country) => ({
                    label: getCountryLabel(currentLanguage, country) ?? '',
                    value: country.key
                  }))}
                  label={'Pays'}
                  selectedOption={conditionalRender(!!field.value, {
                    label:
                      getCountryLabel(
                        currentLanguage,
                        countries?.data.find((country) => country.key === field.value)
                      ) ??
                      field.value ??
                      '',
                    value: field.value
                  })}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
    </InnerBox>
  )
}

export default CseAddressFormSection
